import "./maximetro.css";
import { useDispatch, useSelector } from "react-redux";
import { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import MeasurementContext from "../../../context/MeasurementContext";
import DragDropCompanies from "../../../components/Measurement/DragDropCompanies";
import FrequencyComponent from "../../../components/FilterFrequency/FilterFrequency";
import ChartControls from "../../../components/Measurement/ChartControls";
import { useMeasurementGroups } from "../../../hooks/useMeasurementGroups";
import { useImageExport } from "../../../hooks/useImageExport";
import GroupSelector from "../../../components/Measurement/GroupSelector";
import SendToDashboardModal from "../../../components/Measurement/Consumption/SendToDashboardModal.js";
import {
  generateID,
  paramTagToMeasureType,
} from "../../../helpers/widgetsCharts";
import {
  createWidget,
  getAllWidgets,
} from "../../../redux/apiCalls/widgetCall/widgetCall";
import {
  getInitialDatesFromStorageOrToday,
  updateConsumptionState,
} from "../../../helpers/measurement-secGroup";
import MaximetroMax from "./MaximetroMax.js";
import { autofillFromSingleCurrentDevice } from "../../../helpers/consumptionAutoFromSingleDevice.js";
import MaximetroChart from "../../../components/Charts/MaximetroChart.js";
import { History, Settings } from "lucide-react";
import MaximeterConfigModal from "../../../components/Modals/MaximeterConfigModal";
import useMaximetroData from "../../../hooks/useMaximetroData.js";
import MaximetroMetaBar from "./MaximetroMetaBar.js";
import MaximetroHistoryDrawer from "./MaximetroHistoryDrawer.js";

const Maximetro = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.currentUser);
  const userToken = user?.accessToken;
  const userId = user?.id_user;

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [graph, setGraph] = useState("Line");
  const [firstSelect, setFirstSelect] = useState([]);
  const [devicesSelected, setDevicesSelected] = useState([]);
  const [meterSelected, setMeterSelected] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(true);
  const [frec, setFrec] = useState("");
  const [openDropdown, setOpenDropdown] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [availableBoards, setAvailableBoards] = useState([]);

  const inputRef = useRef(null);
  const exportNameRef = useRef(null);

  const { allGroups, primary, setPrimary, second, setSecond } =
    useMeasurementGroups(dispatch, userToken);

  const currentDevices = useSelector((state) => state.devices.currentDevices);

  const {
    data: maximetroItem,
    viewingVersion,
    setViewingVersion,
    loadCurrent,
    recalculate,
  } = useMaximetroData({
    branchId: firstSelect[0]?.id,
    startDate,
    endDate,
    frec,
    dispatch,
    token: userToken,
  });

  const { captureScreen, exportToPDF, handleImageExport } = useImageExport(
    firstSelect,
    meterSelected,
    startDate,
    endDate,
  );

  useEffect(() => {
    const { startDate: initialStart, endDate: initialEnd } =
      getInitialDatesFromStorageOrToday();

    setStartDate(initialStart);
    setEndDate(initialEnd);
  }, []);

  useEffect(() => {
    updateConsumptionState({ primary, secondary: second });
  }, [primary, second]);

  useEffect(() => {
    if (!userToken) return;

    getAllWidgets(dispatch, userToken).then((widgetsByBoard) => {
      const boards = Object.entries(widgetsByBoard)
        .filter(([_, widgets]) =>
          widgets.some((w) => w.template === "custom" && !w.config),
        )
        .map(([boardName, widgets]) => {
          const placeholder = widgets.find(
            (w) => w.template === "custom" && !w.config,
          );
          const realWidgets = widgets.filter(
            (w) => w.template === "custom" && w.config,
          );

          return {
            name: boardName,
            displayName: placeholder?.name || boardName,
            widgets: realWidgets,
          };
        });

      setAvailableBoards(boards);
    });
  }, [dispatch, userToken]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    autofillFromSingleCurrentDevice({
      currentDevices,
      firstSelect,
      setFirstSelect,
      primary,
      setPrimary,
      secondary: second,
      setSecondary: setSecond,
      devicesSelected,
      setDevicesSelected,
      startDate,
      endDate,
      frec,
      meterSelected,
    });
  }, [
    currentDevices,
    startDate,
    endDate,
    frec,
    meterSelected,
    firstSelect,
    primary,
    second,
    devicesSelected,
  ]);

  const handleValuesChange = (newFrec, newStartDate, newEndDate) => {
    setFrec(newFrec);
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    updateConsumptionState({
      frec: newFrec,
      startDate: newStartDate?.toISOString(),
      endDate: newEndDate?.toISOString(),
    });
  };

  const contextValue = useMemo(
    () => ({ meterSelected, setMeterSelected }),
    [meterSelected],
  );

  const isSendDisabled = useMemo(() => {
    return !(startDate && endDate && meterSelected?.tag);
  }, [startDate, endDate, meterSelected]);

  const handleSendToDashboard = async (selectedBoard, nameDisplay) => {
    const id_widget = generateID();

    const payload = {
      id_widget,
      branchId: firstSelect[0]?.id,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      frec,
      chartType: graph.toLowerCase(),
      template: "custom",
      board: selectedBoard,
      name: nameDisplay,
      id_user: userId,
    };

    await createWidget(dispatch, payload, userToken);
    setModalOpen(false);
  };

  const handleExportClick = () => {
    setShowInput((prev) => !prev);
    setButtonVisible(false);
  };

  const calculateResults = useMemo(() => {
    const values = {};
    return values;
  }, []);

  const contractedPower = useMemo(() => {
    return (
      maximetroItem?.result?.overall?.contracted_power ??
      maximetroItem?.config?.contracted_power ??
      maximetroItem?.config?.potencia_contratada ??
      null
    );
  }, [maximetroItem]);

  const chartData = useMemo(() => {
    if (!maximetroItem) return null;

    return {
      ...maximetroItem,
      contracted_power: contractedPower,
      result: {
        ...maximetroItem.result,
        overall: {
          ...maximetroItem.result?.overall,
          contracted_power: contractedPower,
        },
      },
      series: Array.isArray(maximetroItem.series)
        ? maximetroItem.series.map((point) => ({
            ...point,
            contracted_power:
              point?.contracted_power ?? contractedPower ?? null,
          }))
        : [],
    };
  }, [maximetroItem, contractedPower]);

  const noData = useMemo(() => {
    return (
      chartData?.meta?.no_data === true ||
      chartData?.quality?.status === "NO_DATA" ||
      !chartData?.series?.length
    );
  }, [chartData]);

  return (
    <MeasurementContext.Provider value={contextValue}>
      <div className="container-maximetro">
        <div className="page-content">
          <div className="cont-path-settings">
            <div className="cont-path">
              <p className="path path-cursor" onClick={() => navigate(`/`)}>
                <FormattedMessage id="Dashboard" defaultMessage="Dashboard" />
                {"> "}
              </p>
              <p className="path">
                <FormattedMessage id="Maximetre" defaultMessage="Maxímetro" />
              </p>
            </div>

            {user?.privilege === "master" && (
              <div>
                <button
                  className="config-btn"
                  onClick={() => setConfigModalOpen(true)}
                >
                  <Settings size={20} />
                </button>
                <button
                  className="config-btn"
                  onClick={() => setHistoryOpen(true)}
                >
                  <History size={20} />
                </button>
              </div>
            )}
          </div>

          <div className="measurement-maximetro">
            <DragDropCompanies
              setFirstSelect={setFirstSelect}
              firstSelect={firstSelect}
              maxSelections={1}
            />

            {/* <GroupSelector
              allGroups={allGroups}
              primary={primary}
              setPrimary={setPrimary}
              second={second}
              setSecond={setSecond}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              firstSelect={firstSelect}
            /> */}

            <FrequencyComponent
              onValuesChange={handleValuesChange}
              startDate={startDate}
              endDate={endDate}
              isMaximeter={true}
            />

            <MaximetroMetaBar
              meta={chartData?.meta}
              canRecalculate={user?.privilege === "master"}
              onRecalculate={recalculate}
            />

            {viewingVersion && (
              <div className="maximeter-banner">
                <div>
                  <b>Viendo versión histórica:</b> v{viewingVersion}
                </div>
                <button className="btn-secondary" onClick={loadCurrent}>
                  Volver a vigente
                </button>
              </div>
            )}

            <div id="graph" className="graph-maximetro" ref={inputRef}>
              {!chartData ? null : noData ? (
                <div className="maximetro-empty-state">
                  <div className="maximetro-empty-state-box">
                    <p className="maximetro-empty-title">
                      No se pudo calcular el maxímetro
                    </p>
                    <p className="maximetro-empty-message">
                      {chartData?.meta?.message ||
                        "No hay datos de maxímetro para el período seleccionado."}
                    </p>
                  </div>
                </div>
              ) : (
                <MaximetroChart data={chartData} />
              )}
            </div>

            <ChartControls
              onCaptureScreen={captureScreen}
              onExportPDF={() => exportToPDF(inputRef)}
              showExportInput={showInput}
              onExportClick={handleExportClick}
              onExportSubmit={() =>
                handleImageExport(inputRef, calculateResults)
              }
              buttonVisible={buttonVisible}
              nameInputRef={exportNameRef}
              setModalOpen={setModalOpen}
              containerRef={inputRef}
            />

            <SendToDashboardModal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              onSubmit={handleSendToDashboard}
              boards={availableBoards}
              isSendDisabled={isSendDisabled}
            />

            {!noData && <MaximetroMax data={chartData} />}
          </div>
        </div>
      </div>

      <MaximeterConfigModal
        isOpen={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
      />

      <MaximetroHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        branchId={firstSelect[0]?.id}
        startDate={startDate}
        endDate={endDate}
        dispatch={dispatch}
        token={userToken}
        onSelectVersion={(data) => {
          setViewingVersion(data?.meta?.version_number);
        }}
      />
    </MeasurementContext.Provider>
  );
};

export default Maximetro;